import React, { useEffect, useRef, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { PROVINCES } from "../../Constants";
import { saveManufacturerPreset, deleteManufacturerPreset, getPresetProducts } from "../../API";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import UpdateDeleteBar from "../../components/UpdateDeleteBar";

function parseIds(str) {
  if (!str) return [];
  return str.split(",").map(Number).filter(Boolean);
}

function filterBannersByProvinces(banners, selectedProvinces) {
  if (selectedProvinces.length === 0) return banners;
  const ids = new Set(selectedProvinces.map((p) => p.id));
  return banners.filter((b) => (b.provinces ?? []).some((p) => ids.has(p.id)));
}

function ManufacturerPreset() {
  const { preset, manufacturers, banners } = useLoaderData();
  const navigate = useNavigate();
  const formRef = useRef();

  const [manufacturerId, setManufacturerId] = useState(String(preset?.manufacturerId ?? ""));
  const [selectedProvinces, setSelectedProvinces] = useState(
    PROVINCES.filter((p) => parseIds(preset?.provinceIds).includes(p.id))
  );
  const [selectedBanners, setSelectedBanners] = useState(
    banners.filter((b) => parseIds(preset?.bannerIds).includes(b.id))
  );
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const prevProvincesRef = useRef(selectedProvinces);
  const firstProductLoadRef = useRef(true);

  const filteredBanners = filterBannersByProvinces(banners, selectedProvinces);

  useEffect(() => {
    if (prevProvincesRef.current === selectedProvinces) return;
    prevProvincesRef.current = selectedProvinces;
    setSelectedBanners([]);
  }, [selectedProvinces]);

  useEffect(() => {
    if (!manufacturerId) {
      setProducts([]);
      setSelectedProducts([]);
      return;
    }
    setLoadingProducts(true);
    getPresetProducts(
      parseInt(manufacturerId),
      selectedBanners.map((b) => b.id),
      selectedProvinces.map((p) => p.id)
    )
      .then((list) => {
        setProducts(list);
        if (firstProductLoadRef.current) {
          firstProductLoadRef.current = false;
          const existingIds = new Set(parseIds(preset?.productIds));
          setSelectedProducts(list.filter((p) => existingIds.has(p.id)));
        } else {
          setSelectedProducts((prev) => {
            const availableIds = new Set(list.map((p) => p.id));
            return prev.filter((p) => availableIds.has(p.id));
          });
        }
      })
      .finally(() => setLoadingProducts(false));
  }, [manufacturerId, selectedBanners, selectedProvinces]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e) => {
    e.preventDefault();
    const mfr = manufacturers.find((m) => String(m.id) === manufacturerId);
    await saveManufacturerPreset({
      id: preset?.id,
      manufacturerId: parseInt(manufacturerId),
      manufacturerName: mfr?.name ?? "",
      provinceIds: selectedProvinces.map((p) => p.id).join(","),
      bannerIds: selectedBanners.map((b) => b.id).join(","),
      productIds: selectedProducts.map((p) => p.id).join(","),
    });
    navigate("../manufacturer-presets");
  };

  const handleDelete = async () => {
    await deleteManufacturerPreset(preset.id);
    navigate("../manufacturer-presets");
  };

  const productLabel = (p) =>
    [p.description, p.formatQut, p.format].filter(Boolean).join(" ");

  const isEdit = !!preset?.id;

  return (
    <div className="flex-grow-1 overflow-auto">
      <UpdateDeleteBar
        onDelete={isEdit ? handleDelete : undefined}
        onSave={() => formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}
        title={preset?.manufacturerName || "New Preset"}
      />
      <div className="container-fluid py-4 px-4">
        <form onSubmit={handleSave} ref={formRef}>
          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-medium small">Manufacturer</label>
                  <select
                    className="form-select"
                    value={manufacturerId}
                    onChange={(e) => setManufacturerId(e.target.value)}
                    required
                    disabled={isEdit}
                  >
                    <option value="">Select…</option>
                    {manufacturers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium small">Provinces</label>
                  <DropdownMultiSelect
                    options={PROVINCES}
                    value={selectedProvinces}
                    onChange={setSelectedProvinces}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    placeholder="All provinces"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium small">Banners</label>
                  <DropdownMultiSelect
                    options={filteredBanners}
                    value={selectedBanners}
                    onChange={setSelectedBanners}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    placeholder="All banners"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-medium small">
                    Products
                    {loadingProducts && (
                      <span className="text-muted ms-2 fw-normal">Loading…</span>
                    )}
                  </label>
                  <DropdownMultiSelect
                    options={products}
                    value={selectedProducts}
                    onChange={setSelectedProducts}
                    getOptionLabel={productLabel}
                    getOptionValue={(o) => o.id}
                    placeholder={manufacturerId ? "All products" : "Select a manufacturer first"}
                    disabled={!manufacturerId || loadingProducts}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ManufacturerPreset;
